'use client';

import { useState, useRef, useEffect, type ReactNode } from 'react';
import { ProductImageStudio } from '@/components/product-image-studio';
import { ProductInstagramPostsEditor } from '@/components/instagram/product-instagram-posts-editor';
import Image from 'next/image';
import {
  ArrowDown,
  ArrowUp,
  Bean,
  Egg,
  Fish,
  type LucideIcon,
  Milk,
  Nut,
  Plus,
  Shell,
  Trash2,
  Upload,
  Wheat,
  X as XIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ProductOptionGroupInput } from '@/lib/api';
import { PRODUCT_CATEGORIES } from '@/lib/constants';
import { centsToDollars, dollarsToCents, isPriceDeltaInRange } from '@/lib/option-money';
import { hasProductPrice } from '@sweetspot/shared';

export interface CatalogProductFormData {
  productType: string;
  title: string;
  description: string;
  name2: string;
  description2: string;
  ingredients: string[];
  allergies: string[];
  unit: string;
  price: string;
  minQty: string;
  leadTimeDays: string;
  category: string;
}

export type CatalogProductLike = Partial<{
  productType: 'goods' | 'service' | string | null;
  title: string | null;
  description: string | null;
  name2: string | null;
  description2: string | null;
  ingredients: string[] | null;
  allergies: string[] | null;
  unit: string | null;
  price: string | number | null;
  minQty: string | number | null;
  leadTimeDays: string | number | null;
  category: string | null;
}>;

const GOODS_UNITS = [
  { value: 'each', label: 'each' },
  { value: 'dozen', label: 'dozen' },
  { value: 'pack', label: 'pack' },
];

const SERVICE_UNITS = [
  { value: 'trip', label: 'per trip' },
  { value: 'hour', label: 'per hour' },
  { value: 'day', label: 'per day' },
  { value: 'job', label: 'per job' },
];

const defaultFormData: CatalogProductFormData = {
  productType: 'goods',
  title: '',
  description: '',
  name2: '',
  description2: '',
  ingredients: [],
  allergies: [],
  unit: 'each',
  price: '',
  minQty: '1',
  category: '',
  leadTimeDays: '0',
};

type AllergyOption = {
  key: string;
  label: string;
  Icon: LucideIcon;
};

const POPULAR_ALLERGIES: AllergyOption[] = [
  { key: 'gluten', label: 'Gluten', Icon: Wheat },
  { key: 'dairy', label: 'Dairy', Icon: Milk },
  { key: 'eggs', label: 'Eggs', Icon: Egg },
  { key: 'peanuts', label: 'Peanuts', Icon: Nut },
  { key: 'tree nuts', label: 'Tree Nuts', Icon: Shell },
  { key: 'soy', label: 'Soy', Icon: Bean },
  { key: 'fish', label: 'Fish', Icon: Fish },
  { key: 'shellfish', label: 'Shellfish', Icon: Shell },
];

function normalizeTagList(values: string[]): string[] {
  const seen = new Set<string>();
  const next: string[] = [];
  for (const raw of values) {
    const value = raw.trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    next.push(value);
  }
  return next;
}

function parseTagInput(input: string): string[] {
  return input
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

/* ── Option groups (size, milk, add-ons) ────────────────────────────────── */

const OPTION_NAME_MAX = 40;
const OPTION_GROUPS_MAX = 20;
const OPTIONS_PER_GROUP_MAX = 30;

/** Row state while editing: numbers stay as text so partial input is allowed. */
interface OptionRowDraft {
  key: string;
  id?: string;
  name: string;
  /** Dollars, e.g. "0.75" or "-1". Converted to cents on submit. */
  priceDelta: string;
  isSoldOut: boolean;
}

interface OptionGroupDraft {
  key: string;
  id?: string;
  name: string;
  required: boolean;
  minSelect: string;
  maxSelect: string;
  options: OptionRowDraft[];
}

let draftKeySeq = 0;
const nextDraftKey = () => `draft-${++draftKeySeq}`;

const newOptionDraft = (): OptionRowDraft => ({ key: nextDraftKey(), name: '', priceDelta: '', isSoldOut: false });
const newGroupDraft = (): OptionGroupDraft => ({
  key: nextDraftKey(),
  name: '',
  required: true,
  minSelect: '1',
  maxSelect: '1',
  options: [newOptionDraft()],
});

function toOptionGroupDrafts(groups?: ProductOptionGroupInput[]): OptionGroupDraft[] {
  if (!Array.isArray(groups)) return [];
  return groups.map((group) => ({
    key: nextDraftKey(),
    id: group.id,
    name: group.name ?? '',
    required: Boolean(group.required),
    minSelect: String(group.minSelect ?? 0),
    maxSelect: String(group.maxSelect ?? 1),
    options: (group.options ?? []).map((option) => ({
      key: nextDraftKey(),
      id: option.id,
      name: option.name ?? '',
      priceDelta: option.priceDeltaCents ? centsToDollars(option.priceDeltaCents) : '',
      isSoldOut: Boolean(option.isSoldOut),
    })),
  }));
}

/** Drafts → API shape. Assumes `validateOptionGroups` passed. */
function draftsToOptionGroups(drafts: OptionGroupDraft[]): ProductOptionGroupInput[] {
  return drafts.map((group, groupIndex) => {
    const maxSelect = Math.max(1, parseInt(group.maxSelect, 10) || 1);
    const rawMin = parseInt(group.minSelect, 10);
    const minSelect = group.required
      ? Math.min(maxSelect, Math.max(1, Number.isFinite(rawMin) ? rawMin : 1))
      : Math.min(maxSelect, Math.max(0, Number.isFinite(rawMin) ? rawMin : 0));
    return {
      id: group.id,
      name: group.name.trim(),
      required: group.required,
      minSelect,
      maxSelect,
      sortOrder: groupIndex,
      options: group.options.map((option, optionIndex) => ({
        id: option.id,
        name: option.name.trim(),
        priceDeltaCents: dollarsToCents(option.priceDelta) ?? 0,
        isSoldOut: option.isSoldOut,
        sortOrder: optionIndex,
      })),
    };
  });
}

function validateOptionGroups(drafts: OptionGroupDraft[]): Record<string, string> {
  const errs: Record<string, string> = {};
  drafts.forEach((group, gi) => {
    const name = group.name.trim();
    if (!name) errs[`og.${gi}.name`] = 'Group name is required';
    else if (name.length > OPTION_NAME_MAX) errs[`og.${gi}.name`] = `Keep it under ${OPTION_NAME_MAX} characters`;
    const min = parseInt(group.minSelect, 10);
    const max = parseInt(group.maxSelect, 10);
    if (!Number.isInteger(max) || max < 1) errs[`og.${gi}.range`] = 'Max must be at least 1';
    else if (Number.isInteger(min) && min > max) errs[`og.${gi}.range`] = 'Min cannot be more than max';
    else if (max > group.options.length) errs[`og.${gi}.range`] = 'Max cannot exceed the number of options';
    if (group.options.length === 0) errs[`og.${gi}.options`] = 'Add at least one option';
    group.options.forEach((option, oi) => {
      const optionName = option.name.trim();
      if (!optionName) errs[`og.${gi}.opt.${oi}.name`] = 'Option name is required';
      else if (optionName.length > OPTION_NAME_MAX) errs[`og.${gi}.opt.${oi}.name`] = `Keep it under ${OPTION_NAME_MAX} characters`;
      const cents = dollarsToCents(option.priceDelta);
      if (cents === null) errs[`og.${gi}.opt.${oi}.price`] = 'Enter a dollar amount';
      else if (!isPriceDeltaInRange(cents)) errs[`og.${gi}.opt.${oi}.price`] = 'Within ±$100';
    });
  });
  return errs;
}

function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export function toCatalogProductFormData(data?: CatalogProductLike): CatalogProductFormData {
  if (!data) return { ...defaultFormData };
  const toNumericString = (value: string | number | null | undefined, fallback: string) => {
    if (value === null || value === undefined || value === '') return fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? String(parsed) : fallback;
  };

  return {
    productType: data.productType ?? defaultFormData.productType,
    title: data.title ?? defaultFormData.title,
    description: data.description ?? defaultFormData.description,
    name2: data.name2 ?? defaultFormData.name2,
    description2: data.description2 ?? defaultFormData.description2,
    ingredients: normalizeTagList(Array.isArray(data.ingredients) ? data.ingredients : []),
    allergies: normalizeTagList(Array.isArray(data.allergies) ? data.allergies : []),
    unit: data.unit ?? defaultFormData.unit,
    price: toNumericString(data.price, defaultFormData.price),
    minQty: toNumericString(data.minQty, defaultFormData.minQty),
    leadTimeDays: toNumericString(data.leadTimeDays, defaultFormData.leadTimeDays),
    category: data.category ?? defaultFormData.category,
  };
}

interface CatalogProductFormProps {
  productId?: string;
  businessId?: string;
  initialData?: Partial<CatalogProductFormData>;
  existingImageUrl?: string | null;
  initialInstagramPhotoId?: string | null;
  canManageInstagramPhoto?: boolean;
  /** Option groups loaded for an existing product (GET …/option-groups). */
  initialOptionGroups?: ProductOptionGroupInput[];
  onSubmit: (data: {
    productType: 'goods' | 'service';
    title: string;
    description?: string;
    name2: string | null;
    description2: string | null;
    ingredients: string[];
    allergies: string[];
    unit: string;
    price: number;
    minQty: number;
    category?: string;
    imageFile?: File;
    /** Complete option set for the product — save with PUT …/option-groups. */
    optionGroups: ProductOptionGroupInput[];
  }) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
  loadingLabel?: string;
  afterMinQtySection?: ReactNode;
}

export function CatalogProductForm({
  productId,
  businessId,
  initialData,
  existingImageUrl,
  canManageInstagramPhoto = false,
  initialOptionGroups,
  onSubmit,
  onCancel,
  submitLabel = 'Create',
  loadingLabel = 'Creating...',
  afterMinQtySection,
}: CatalogProductFormProps) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState<CatalogProductFormData>(() => toCatalogProductFormData(initialData));
  const [priceTbd, setPriceTbd] = useState(() =>
    initialData?.price != null && initialData.price !== '' && !hasProductPrice(initialData.price),
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(existingImageUrl ?? null);
  const [ingredientInput, setIngredientInput] = useState('');
  const [optionGroups, setOptionGroups] = useState<OptionGroupDraft[]>(() => toOptionGroupDrafts(initialOptionGroups));
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => () => { if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview); }, [imagePreview]);

  const isService = form.productType === 'service';
  const unitOptions = isService ? SERVICE_UNITS : GOODS_UNITS;

  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!form.title.trim()) errs.title = 'Title is required';
    if (!priceTbd && !hasProductPrice(form.price)) errs.price = 'Enter a price greater than 0 or select TBD';
    if (!isService && form.minQty && parseInt(form.minQty, 10) < 1) errs.minQty = 'Min quantity must be at least 1';
    Object.assign(errs, validateOptionGroups(optionGroups));
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const mergedIngredients = normalizeTagList([
      ...form.ingredients,
      ...parseTagInput(ingredientInput),
    ]);
    if (mergedIngredients.length !== form.ingredients.length) {
      setForm((current) => ({ ...current, ingredients: mergedIngredients }));
    }
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setLoading(true);
    try {
      await onSubmit({
        productType: form.productType as 'goods' | 'service',
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        name2: form.name2.trim() || null,
        description2: form.description2.trim() || null,
        ingredients: mergedIngredients,
        allergies: normalizeTagList(form.allergies),
        unit: form.unit,
        price: priceTbd ? 0 : Number(form.price),
        minQty: isService ? 1 : (parseInt(form.minQty, 10) || 1),
        category: form.category.trim() || undefined,
        imageFile: imageFile || undefined,
        optionGroups: draftsToOptionGroups(optionGroups),
      });
    } catch (err) {
      console.error('Form submission failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const set = (field: keyof CatalogProductFormData, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => { const next = { ...e }; delete next[field]; return next; });
  };

  const FieldError = ({ field }: { field: string }) =>
    errors[field] ? <p className="mt-1 text-xs text-destructive">{errors[field]}</p> : null;

  const addIngredients = (raw: string) => {
    const values = parseTagInput(raw);
    if (!values.length) return;
    setForm((current) => ({
      ...current,
      ingredients: normalizeTagList([...current.ingredients, ...values]),
    }));
  };

  const removeIngredient = (ingredient: string) => {
    setForm((current) => ({
      ...current,
      ingredients: current.ingredients.filter((item) => item !== ingredient),
    }));
  };

  const toggleAllergy = (allergy: string) => {
    setForm((current) => {
      const exists = current.allergies.some((item) => item.toLowerCase() === allergy.toLowerCase());
      return {
        ...current,
        allergies: exists
          ? current.allergies.filter((item) => item.toLowerCase() !== allergy.toLowerCase())
          : normalizeTagList([...current.allergies, allergy]),
      };
    });
  };

  // Option-group errors are keyed by position, so any edit clears them and
  // the next submit re-validates from scratch.
  const updateOptionGroups = (updater: (groups: OptionGroupDraft[]) => OptionGroupDraft[]) => {
    setOptionGroups(updater);
    setErrors((current) => {
      if (!Object.keys(current).some((key) => key.startsWith('og.'))) return current;
      const next: Record<string, string> = {};
      for (const [key, value] of Object.entries(current)) if (!key.startsWith('og.')) next[key] = value;
      return next;
    });
  };
  const patchGroup = (gi: number, patch: Partial<OptionGroupDraft>) =>
    updateOptionGroups((groups) => groups.map((group, i) => (i === gi ? { ...group, ...patch } : group)));
  const patchOption = (gi: number, oi: number, patch: Partial<OptionRowDraft>) =>
    updateOptionGroups((groups) =>
      groups.map((group, i) =>
        i === gi
          ? { ...group, options: group.options.map((option, j) => (j === oi ? { ...option, ...patch } : option)) }
          : group,
      ),
    );
  const addGroup = () => updateOptionGroups((groups) => [...groups, newGroupDraft()]);
  const removeGroup = (gi: number) => updateOptionGroups((groups) => groups.filter((_, i) => i !== gi));
  const moveGroup = (gi: number, delta: -1 | 1) => updateOptionGroups((groups) => moveItem(groups, gi, gi + delta));
  const addOption = (gi: number) =>
    updateOptionGroups((groups) =>
      groups.map((group, i) => (i === gi ? { ...group, options: [...group.options, newOptionDraft()] } : group)),
    );
  const removeOption = (gi: number, oi: number) =>
    updateOptionGroups((groups) =>
      groups.map((group, i) => (i === gi ? { ...group, options: group.options.filter((_, j) => j !== oi) } : group)),
    );
  const moveOption = (gi: number, oi: number, delta: -1 | 1) =>
    updateOptionGroups((groups) =>
      groups.map((group, i) => (i === gi ? { ...group, options: moveItem(group.options, oi, oi + delta) } : group)),
    );
  const setRequired = (gi: number, required: boolean) =>
    updateOptionGroups((groups) =>
      groups.map((group, i) => {
        if (i !== gi) return group;
        const min = parseInt(group.minSelect, 10);
        return {
          ...group,
          required,
          minSelect: required && (!Number.isInteger(min) || min < 1) ? '1' : group.minSelect,
        };
      }),
    );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Image Upload */}
      <div>
        <Label className="mb-2 block">Product Image</Label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setImageFile(file);
              const url = URL.createObjectURL(file);
              setImagePreview(url);
            }
          }}
        />
        {imagePreview ? (
          <div className="group/img relative w-full h-32 rounded-lg overflow-hidden bg-muted">
            <Image src={imagePreview} alt="Preview" fill sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" className="object-contain" />
            <button
              type="button"
              className="absolute inset-0 flex items-center justify-center gap-1.5 bg-black/40 text-white text-xs font-medium opacity-0 group-hover/img:opacity-100 transition-opacity"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4" />
              Change image
            </button>
            <button
              type="button"
              aria-label="Remove image"
              className="absolute top-1.5 right-1.5 rounded-full bg-background/80 p-1 hover:bg-background transition-colors z-10"
              onClick={() => {
                setImageFile(null);
                setImagePreview(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed py-6 text-sm text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors"
          >
            <Upload className="h-4 w-4" />
            Upload image
          </button>
        )}
        {productId && <ProductImageStudio productId={productId} businessId={businessId} disabled={loading}
          onSelect={file => { setImageFile(file); setImagePreview(URL.createObjectURL(file)); }} />}
        {productId && businessId && <ProductInstagramPostsEditor productId={productId} businessId={businessId}
          canEdit={canManageInstagramPhoto} disabled={loading} />}
      </div>

      <div>
        <Label htmlFor="title">{isService ? 'Service name' : 'Title'} *</Label>
        <Input
          id="title"
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          placeholder={isService ? 'e.g., Dessert Delivery - Gangnam Area' : 'e.g., Chocolate Chip Cookies'}
          required
        />
        <FieldError field="title" />
      </div>
      <div>
        <Label htmlFor="description">Description</Label>
        <textarea
          id="description"
          className="flex min-h-[80px] w-full rounded-md border bg-transparent px-3 py-2 text-sm"
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder={isService
            ? 'e.g., Refrigerated delivery, available mornings, bike courier'
            : 'Optional details about this product...'
          }
        />
      </div>
      <fieldset className="space-y-3 rounded-lg border border-dashed p-3">
        <legend className="px-1 text-sm font-medium">Second language (optional)</legend>
        <p className="text-xs text-muted-foreground">
          Add translated copy to show alongside the primary name and description on your site.
        </p>
        <div>
          <Label htmlFor="name2">Translated {isService ? 'service name' : 'name'}</Label>
          <Input
            id="name2"
            value={form.name2}
            onChange={(e) => set('name2', e.target.value)}
            placeholder="e.g., 초콜릿 칩 쿠키"
            maxLength={200}
          />
        </div>
        <div>
          <Label htmlFor="description2">Translated description</Label>
          <textarea
            id="description2"
            className="flex min-h-[80px] w-full rounded-md border bg-transparent px-3 py-2 text-sm"
            value={form.description2}
            onChange={(e) => set('description2', e.target.value)}
            placeholder="Optional translated details..."
            maxLength={2000}
          />
        </div>
      </fieldset>
      <div className="space-y-2">
        <Label htmlFor="ingredients-input">Ingredients</Label>
        <div className="rounded-md border bg-background px-2 py-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {form.ingredients.map((ingredient) => (
              <span
                key={ingredient}
                className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs"
              >
                {ingredient}
                <button
                  type="button"
                  onClick={() => removeIngredient(ingredient)}
                  className="rounded-full p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                  aria-label={`Remove ingredient ${ingredient}`}
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
            <input
              id="ingredients-input"
              value={ingredientInput}
              onChange={(event) => setIngredientInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === ',' || event.key === 'Enter') {
                  event.preventDefault();
                  addIngredients(ingredientInput);
                  setIngredientInput('');
                }
              }}
              onBlur={() => {
                addIngredients(ingredientInput);
                setIngredientInput('');
              }}
              placeholder="Type ingredient, press comma"
              className="h-7 min-w-[180px] flex-1 border-0 bg-transparent px-1 text-sm outline-none"
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Use comma to add each ingredient.
        </p>
      </div>
      <div className="space-y-2">
        <Label className="block">Allergies</Label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {POPULAR_ALLERGIES.map((allergy) => {
            const selected = form.allergies.some(
              (item) => item.toLowerCase() === allergy.key.toLowerCase(),
            );
            return (
              <button
                key={allergy.key}
                type="button"
                onClick={() => toggleAllergy(allergy.key)}
                className={[
                  'flex items-center gap-2 rounded-md border px-2 py-2 text-left text-xs transition-colors',
                  selected
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-muted text-muted-foreground hover:border-primary/40 hover:text-foreground',
                ].join(' ')}
              >
                <allergy.Icon className="h-4 w-4 shrink-0" />
                <span>{allergy.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="price">{priceTbd ? 'Price' : 'Price *'}</Label>
          <Input
            id="price"
            type="number"
            step="0.01"
            min="0.01"
            value={priceTbd ? '' : form.price}
            onChange={(e) => set('price', e.target.value)}
            placeholder={priceTbd ? 'Coming soon' : '0.00'}
            required={!priceTbd}
            disabled={priceTbd || loading}
          />
          <label className="mt-2 flex min-h-6 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={priceTbd}
              disabled={loading}
              aria-describedby="price-tbd-help"
              className="h-4 w-4 accent-primary"
              onChange={(e) => {
                setPriceTbd(e.target.checked);
                if (!hasProductPrice(form.price)) set('price', '');
                setErrors((current) => { const next = { ...current }; delete next.price; return next; });
              }}
            />
            TBD
          </label>
          <p id="price-tbd-help" className="mt-1 text-xs text-muted-foreground">
            Show Coming soon and pause orders until you set a price.
          </p>
          <FieldError field="price" />
        </div>
        <div>
          <Label htmlFor="unit">Unit</Label>
          <select
            id="unit"
            className="flex h-10 w-full rounded-md border bg-transparent px-3 py-2 text-sm"
            value={form.unit}
            onChange={(e) => set('unit', e.target.value)}
          >
            {unitOptions.map((u) => (
              <option key={u.value} value={u.value}>{u.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Category (main form) */}
      <div>
        <Label htmlFor="category">Category</Label>
        <Select
          value={form.category || undefined}
          onValueChange={(value) => set('category', value)}
        >
          <SelectTrigger id="category">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {/* An ordering-site product's category is its menu section name
                ("Signature", "Matcha"), which is not in PRODUCT_CATEGORIES.
                Without this the trigger reads "Select..." and one stray click
                rewrites the section, moving the item on the live site. */}
            {form.category && !PRODUCT_CATEGORIES.some((c) => c.value === form.category) && (
              <SelectItem value={form.category}>{form.category}</SelectItem>
            )}
            {PRODUCT_CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Goods-specific: min order quantity */}
      {!isService && (
        <div>
          <Label htmlFor="minQty">Min order quantity</Label>
          <Input
            id="minQty"
            type="number"
            min="1"
            value={form.minQty}
            onChange={(e) => set('minQty', e.target.value)}
          />
          <FieldError field="minQty" />
        </div>
      )}
      {afterMinQtySection ?? null}

      {/* Option groups: what customers pick on the ordering site */}
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <Label className="block">Options</Label>
            <p className="text-xs text-muted-foreground">
              Choices customers pick when ordering — size, milk, add-ons.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 gap-1"
            onClick={addGroup}
            disabled={optionGroups.length >= OPTION_GROUPS_MAX}
          >
            <Plus className="h-3.5 w-3.5" />
            Add group
          </Button>
        </div>

        {optionGroups.map((group, gi) => (
          <div key={group.key} className="space-y-3 rounded-lg border p-3 transition-colors">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <Input
                  value={group.name}
                  onChange={(e) => patchGroup(gi, { name: e.target.value })}
                  placeholder="Group name, e.g., Size"
                  aria-label={`Option group ${gi + 1} name`}
                  maxLength={OPTION_NAME_MAX}
                />
                <FieldError field={`og.${gi}.name`} />
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label="Move group up" disabled={gi === 0} onClick={() => moveGroup(gi, -1)}>
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label="Move group down" disabled={gi === optionGroups.length - 1} onClick={() => moveGroup(gi, 1)}>
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-destructive" aria-label="Remove group" onClick={() => removeGroup(gi)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              <label className="flex cursor-pointer select-none items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={group.required}
                  onChange={(e) => setRequired(gi, e.target.checked)}
                />
                Required
              </label>
              <label className="flex items-center gap-1.5 text-muted-foreground">
                Min
                <Input
                  type="number"
                  inputMode="numeric"
                  min={group.required ? 1 : 0}
                  max={OPTIONS_PER_GROUP_MAX}
                  className="h-8 w-16 text-foreground"
                  value={group.minSelect}
                  onChange={(e) => patchGroup(gi, { minSelect: e.target.value })}
                  aria-label="Minimum selections"
                />
              </label>
              <label className="flex items-center gap-1.5 text-muted-foreground">
                Max
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={OPTIONS_PER_GROUP_MAX}
                  className="h-8 w-16 text-foreground"
                  value={group.maxSelect}
                  onChange={(e) => patchGroup(gi, { maxSelect: e.target.value })}
                  aria-label="Maximum selections"
                />
              </label>
            </div>
            <FieldError field={`og.${gi}.range`} />

            <div className="space-y-2">
              {group.options.map((option, oi) => (
                <div key={option.key} className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <Input
                      value={option.name}
                      onChange={(e) => patchOption(gi, oi, { name: e.target.value })}
                      placeholder="Option, e.g., Large"
                      aria-label={`Option ${oi + 1} name`}
                      maxLength={OPTION_NAME_MAX}
                    />
                    <FieldError field={`og.${gi}.opt.${oi}.name`} />
                  </div>
                  <div className="w-24 shrink-0">
                    <div className="relative">
                      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                      <Input
                        inputMode="decimal"
                        className="pl-5"
                        value={option.priceDelta}
                        onChange={(e) => patchOption(gi, oi, { priceDelta: e.target.value })}
                        placeholder="0.00"
                        aria-label="Price adjustment in dollars"
                      />
                    </div>
                    <FieldError field={`og.${gi}.opt.${oi}.price`} />
                  </div>
                  <button
                    type="button"
                    aria-pressed={option.isSoldOut}
                    onClick={() => patchOption(gi, oi, { isSoldOut: !option.isSoldOut })}
                    className={[
                      'h-10 shrink-0 rounded-md border px-2 text-xs transition-colors',
                      option.isSoldOut
                        ? 'border-destructive/40 bg-destructive/10 text-destructive'
                        : 'border-muted text-muted-foreground hover:border-primary/40 hover:text-foreground',
                    ].join(' ')}
                  >
                    {option.isSoldOut ? 'Sold out' : 'In stock'}
                  </button>
                  <div className="flex shrink-0 items-center gap-0.5">
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-8" aria-label="Move option up" disabled={oi === 0} onClick={() => moveOption(gi, oi, -1)}>
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-8" aria-label="Move option down" disabled={oi === group.options.length - 1} onClick={() => moveOption(gi, oi, 1)}>
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-8 text-muted-foreground hover:text-destructive" aria-label="Remove option" onClick={() => removeOption(gi, oi)}>
                      <XIcon className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              <FieldError field={`og.${gi}.options`} />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="gap-1 text-muted-foreground"
                onClick={() => addOption(gi)}
                disabled={group.options.length >= OPTIONS_PER_GROUP_MAX}
              >
                <Plus className="h-3.5 w-3.5" />
                Add option
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 pt-4">
        <Button type="submit" disabled={loading}>
          {loading ? loadingLabel : submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
